import React from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';

import { MoreVertical, PencilLine, Trash2, Copy } from "lucide-react";
import { createPortal } from "react-dom";

// Map level string → numeric score 1–5 (0 if missing/unassessed)
const levelToScore = (value) => {
  if (!value) return 0;
  const v = String(value).toLowerCase();

  switch (v) {
    case "insignificant":
      return 1;
    case "minor":
      return 2;
    case "moderate":
      return 3;
    case "major":
      return 4;
    case "catastrophe":
      return 5;
    case "unassessed":
      return 0;
    default:
      return 0;
  }
};

const getCiaScoreColor = (score) => {
  if (!score) return "text-gray-400";
  if (score >= 75) return "text-red-600";        // high risk
  if (score >= 40) return "text-amber-600";      // medium
  return "text-emerald-600";                     // low
};

const AssetTable = ({ data, totalPagesServer, currentPage2, pageSize2, onEdit, onDelete, onDuplicate }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        width: '5%',
        Cell: ({ value }) => <span>A-{value}</span>,
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: '20%',
        Cell: ({ value, row }) => (
          <div className="flex flex-col">
            <span>{value}</span>
            {row.original.department_name && (
              <span className="text-xs text-gray-400 mt-0.5">{row.original.department_name}</span>
            )}
          </div>
        ),
      },
      {
        Header: 'Category',
        accessor: 'category',
        width: '10%',
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        width: '9%',
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-sm ${
              value === 'active'
                ? 'bg-green-100 text-green-800'
                : value === 'inactive'
                ? 'bg-gray-100 text-gray-800'
                : value === 'retired'
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        ),
      },
      // {
      //   Header: 'Department',
      //   accessor: 'department',
      //   width: '8%',
      //   Cell: ({ value }) => <span>{value || '-'}</span>,
      // },
      {
        Header: 'Information Types',
        accessor: 'information_types',
        width: '12%',
        Cell: ({ value }) => {
          if (!value) return <span>-</span>;
          let types = [];
          try {
            types = typeof value === 'string' ? JSON.parse(value) : value;
          } catch (e) {
            types = [];
          }
          if (!Array.isArray(types) || types.length === 0) return <span>-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {types.slice(0, 2).map((type, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                  {type}
                </span>
              ))}
              {types.length > 2 && (
                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                  +{types.length - 2}
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: 'Sensitivity',
        accessor: 'sensitivity_level',
        width: '11%',
        Cell: ({ value }) => (
          <span
            className={`px-2 py-1 rounded-full text-sm ${
              value === 'public'
                ? 'bg-blue-100 text-blue-800'
                : value === 'internal'
                ? 'bg-green-100 text-green-800'
                : value === 'confidential'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        ),
      },
      {
        Header: 'Owner',
        accessor: 'owner_id',
        width: '10%',
        Cell: ({ row }) => (
          <span>
            {row.original.first_name && row.original.last_name
              ? `${row.original.first_name} ${row.original.last_name}`
              : 'Unassigned'}
          </span>
        ),
      },

      // 🔹 NEW: CIA Score column (replaces 3 separate CIA columns)
      {
        Header: 'CIA Score',
        id: 'cia_score',
        width: '8%',
        Cell: ({ row }) => {
          const cVal = row.original.confidentiality;
          const iVal = row.original.integrity;
          const aVal = row.original.availability;

          const cScore = levelToScore(cVal);
          const iScore = levelToScore(iVal);
          const aScore = levelToScore(aVal);

          const totalScore =
            cScore > 0 && iScore > 0 && aScore > 0
              ? cScore * iScore * aScore
              : 0;

          const scoreColor = getCiaScoreColor(totalScore);

          const cLabel = cScore || '-';
          const iLabel = iScore || '-';
          const aLabel = aScore || '-';

          return (
            <div className="flex flex-col text-xs">
              <span className={`font-semibold ${scoreColor}`}>
                {totalScore ? `${totalScore}/125` : 'Unassessed'}
              </span>
              <span className="text-[11px] text-gray-500 mt-0.5">
                C:{cLabel} I:{iLabel} A:{aLabel}
              </span>
            </div>
          );
        },
      },

      {
        Header: "Actions",
        id: "actions",
        width: "7%",
        Cell: ({ row }) => (
          <RowActionsMenu
            row={row}
            onEdit={onEdit}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        ),
      },


    ],
    [onEdit, onDelete]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: data || [],
      initialState: { pageIndex: 0 },
    },
    useSortBy,
    usePagination
  );
return (
  <div className="relative shadow-lg border border-gray-200 rounded-xl bg-transparent">
    {/* Inner card: actually draws the white rounded shape */}
    <div className="rounded-xl bg-white overflow-hidden">
      {/* scroll wrapper only handles x-scroll */}
      <div className="overflow-x-auto">
        <table
          {...getTableProps()}
          className="min-w-full bg-white"
        >
          <thead className="bg-gray-50">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={`px-6 py-3 text-sm font-medium text-gray-500 tracking-wider whitespace-nowrap
                      ${
                        column.id === "actions"
                          ? "text-center"
                          : "text-left cursor-pointer"
                      }`}
                    style={{ width: column.width }}
                  >
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            className="bg-white divide-y divide-gray-200"
          >
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps({
                    onClick: () => onEdit(row.original),
                  })}
                  className="group cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${
                        cell.column.id === "actions" ? "text-right" : ""
                      }`}
                      style={{ overflow: 'visible', maxWidth: 'none', textOverflow: 'unset' }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);


};


const RowActionsMenu = ({ row, onEdit, onDelete, onDuplicate }) => {
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });

  const close = React.useCallback(() => setOpen(false), []);

  const toggle = () => {
    if (!btnRef.current) return;

    const r = btnRef.current.getBoundingClientRect();
    // menu appears under the button, right-aligned
    setPos({
      top: r.bottom + 6,
      left: r.right - 144, // ~w-36 (9rem = 144px)
    });

    setOpen(v => !v);
  };

  React.useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };

    const onEsc = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, close]);

  return (
    <div
      className="flex items-center justify-end"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="z-[9999] w-36 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
            style={{ position: "fixed", top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                close();
                onEdit(row.original);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <PencilLine className="w-4 h-4" />
              Edit
            </button>

            <button
              type="button"
              onClick={() => {
                close();
                onDuplicate(row.original.id);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>

            <button
              type="button"
              onClick={() => {
                close();
                onDelete(row.original);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AssetTable;
